// The League chip's behaviour on ?live=1, shared by Ranking, Filter, Comparison and Player
// Similarity (Ben, 2026-10-16; rules.md "The League chip"). Always there: untouched it's the canvas
// player's league, shown quiet (like the Stats tile's Season chip) and following that player; once
// the user picks other leagues it stays. Removing it (✕) empties the module — no league, no data —
// until a league is added or (Ranking) a player is found (Ben, 2026-10-16: switching from one
// player's league to another's in two clicks). Empty never means the whole world. The snapshot
// site keeps the prototype's own chips.

import { useCallback, useEffect, useRef } from "react";
import type { ActiveFilters } from "../engines/stats-filter-kit.js";
import { getModState, setModState } from "../modules/bridge.js";
import { LIVE } from "../live/flag.js";

const QUIET_LEAGUE: ReadonlySet<string> = new Set(["league"]);

/** No league to show: the chip removed (✕), or added back with nothing ticked yet. */
export function isLeagueEmpty(mod: HTMLElement, active: ActiveFilters, quietKey: string): boolean {
  const v = active.league;
  return LIVE && (isLeagueCleared(mod, active, quietKey) || (Array.isArray(v) && v.length === 0));
}

/** Did the user remove the League chip (✕), leaving the module empty? Read before the module's
 *  data hook (which needs it); `quietKey` as `useLeagueChip`'s. */
export function isLeagueCleared(mod: HTMLElement, active: ActiveFilters, quietKey: string): boolean {
  return LIVE && getModState<boolean>(mod, `${quietKey}Cleared`) === true && active.league === undefined;
}

/**
 * `active` is the module's chip state (edited in place by the chip bar); `quietKey` the module's
 * own `_`-state flag for "untouched" (saved with Undo and dashboards). Returns the chip bar's
 * `onChange` (it wraps the module's own `bump`) and its `quietKeys`.
 */
export function useLeagueChip(
  mod: HTMLElement,
  active: ActiveFilters,
  defaultLeague: string | undefined,
  version: number,
  bump: () => void,
  quietKey: string,
): { onChipsChange: () => void; quietKeys: ReadonlySet<string> | undefined; cleared: boolean } {
  // The user's ✕ is kept in the module's state (Undo, dashboards): `<quietKey>Cleared`.
  const clearedKey = `${quietKey}Cleared`;
  const cleared = isLeagueCleared(mod, active, quietKey);
  const before = useRef(active.league);
  useEffect(() => {
    if (!LIVE || defaultLeague === undefined) return;
    if (getModState<boolean>(mod, clearedKey) === true && active.league === undefined) return; // left empty on purpose
    const quiet = getModState<boolean>(mod, quietKey) === true;
    if (active.league === undefined || (quiet && active.league !== defaultLeague)) {
      active.league = defaultLeague;
      setModState(mod, quietKey, true);
      bump();
    }
  }, [mod, active, defaultLeague, version, bump, quietKey, clearedKey]);
  const onChipsChange = useCallback(() => {
    if (LIVE) {
      // ✕ on the League chip: leave the module empty (no default back).
      if (before.current !== undefined && active.league === undefined) {
        setModState(mod, clearedKey, true);
        setModState(mod, quietKey, false);
      } else if (active.league !== undefined) {
        setModState(mod, clearedKey, undefined);
        // A user edit of the League chip makes it theirs (no longer quiet).
        if (active.league !== defaultLeague) setModState(mod, quietKey, false);
      }
      before.current = active.league;
    }
    bump();
  }, [mod, active, defaultLeague, bump, quietKey, clearedKey]);
  before.current = active.league;
  const quiet = LIVE && getModState<boolean>(mod, quietKey) === true;
  return { onChipsChange, quietKeys: quiet ? QUIET_LEAGUE : undefined, cleared };
}
